import React from 'react';
import {
  Box,
  LinearProgress,
  Paper,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { Scrollable } from '../Scrollable';
import { StatusBox, StatusIcon } from 'components/ui/StatusIcon';
import * as Types from 'types/types';
import { Node } from './vectorData';

function renderLoadingState(
  vectorStatus: string,
  vectorStatusType: Types.ApiActivityType | null
): React.ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <StatusBox>
        {vectorStatusType && <StatusIcon type={vectorStatusType} />}
        <Typography variant="body2" sx={{ ml: 1 }}>
          {vectorStatus || 'Loading visualization...'}
        </Typography>
      </StatusBox>
      {vectorStatusType && (
        <Box sx={{ width: '100%', maxWidth: 400, mt: 1 }}>
          <LinearProgress />
        </Box>
      )}
    </Box>
  );
}

function renderNodeMetaPanel(node: Node, isMobile: boolean): React.ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        fontSize: '0.75rem',
        flexDirection: 'column',
        flexGrow: 1,
        maxWidth: '100%',
        flexBasis: 1,
        maxHeight: 'min-content',
      }}
    >
      <TableContainer component={Paper} sx={{ mb: isMobile ? 1 : 0, mr: isMobile ? 0 : 1 }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableBody
            sx={{
              '& td': { verticalAlign: 'top', fontSize: '0.75rem' },
              '& td:first-of-type': { whiteSpace: 'nowrap', width: '1rem' },
            }}
          >
            <MetaRow label="Type" value={`${node.emoji} ${node.docType}`} />
            {node.source_file !== undefined && (
              <MetaRow label="File" value={node.source_file.replace(/^.*\//, '')} />
            )}
            {node.path !== undefined && <MetaRow label="Section" value={node.path} />}
            {node.distance !== undefined && (
              <MetaRow label="Distance" value={String(node.distance)} />
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {node.content !== '' && node.content !== undefined && (
        <Paper
          elevation={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #808080',
            minHeight: 'fit-content',
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', background: '#404040', p: 1, color: 'white' }}>
            Vector Embedded Content
          </Box>
          <Box sx={{ display: 'flex', p: 1, flexGrow: 1 }}>{node.content}</Box>
        </Paper>
      )}
    </Box>
  );
}

function MetaRow({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <TableCell>{value}</TableCell>
    </TableRow>
  );
}

function renderNodeFullContent(node: Node): React.ReactElement | null {
  if (!node.fullContent) return null;
  return (
    <Scrollable
      autoscroll={false}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        m: 0,
        p: 0.5,
        pl: 1,
        flexShrink: 1,
        position: 'relative',
        maxWidth: '100%',
      }}
    >
      {node.fullContent.split('\n').map((line, idx) => {
        const lineNum = idx + 1 + node.chunkBegin;
        const bgColor = lineNum > node.lineBegin && lineNum <= node.lineEnd ? '#f0f0f0' : 'auto';
        return (
          <ContentLine
            key={lineNum}
            line={line}
            lineNum={lineNum}
            sx={{ backgroundColor: bgColor }}
          />
        );
      })}
      {!node.lineBegin && (
        <pre style={{ margin: 0, padding: 0, border: 'none' }}>{node.content}</pre>
      )}
    </Scrollable>
  );
}

function ContentLine({
  line,
  lineNum,
  sx,
}: {
  line: string;
  lineNum: number;
  sx: SxProps;
}): React.ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        borderBottom: '1px solid #d0d0d0',
        ':first-of-type': { borderTop: '1px solid #d0d0d0' },
        ...sx,
      }}
    >
      <Box
        sx={{
          fontFamily: 'courier',
          fontSize: '0.8rem',
          minWidth: '2rem',
          pt: '0.1rem',
          align: 'left',
          verticalAlign: 'top',
        }}
      >
        {lineNum}
      </Box>
      <pre style={{ margin: 0, padding: 0, border: 'none', minHeight: '1rem', overflow: 'hidden' }}>
        {line || ' '}
      </pre>
    </Box>
  );
}

export { renderLoadingState, renderNodeFullContent, renderNodeMetaPanel };
